import { backendApis } from '@/backendApis';
import { getEnvVar } from '@/env';
import { integrationKeys } from '@/features/integrations/integrations.query';
import { trackToolkitConnected } from '@/integrations/integrations.utils';
import { currentPageReturnTo } from '@/integrations/oauth-return';
import { queryClient } from '@/query/client';

const OAUTH_POPUP_WIDTH = 560;
const OAUTH_POPUP_HEIGHT = 700;
const OAUTH_POLL_INTERVAL_MS = 4000;
const OAUTH_TIMEOUT_MS = 5 * 60 * 1000;

export const openOAuthPopup = (url: string): Window | null => {
  const left = window.screenX + (window.outerWidth - OAUTH_POPUP_WIDTH) / 2;
  const top = window.screenY + (window.outerHeight - OAUTH_POPUP_HEIGHT) / 2;
  return window.open(
    url,
    'littlebird-oauth',
    `popup,width=${OAUTH_POPUP_WIDTH},height=${OAUTH_POPUP_HEIGHT},left=${left},top=${top}`
  );
};

/**
 * Resolves once the OAuth flow settles. The backend's OAuth result page posts
 * a `littlebird:oauth` message to the opener and closes itself (fast path);
 * polling the connected accounts covers popups that lose the opener reference.
 * A closed popup is not treated as failure — it may close after a successful
 * redirect before the connection shows up — so polling runs until the timeout.
 */
const waitForOAuthCompletion = (
  toolkitName: string,
  accountId: string | null
): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    let settled = false;

    const settle = (success: boolean) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      clearInterval(pollInterval);
      clearTimeout(timeout);
      resolve(success);
    };

    const checkAccount = async () => {
      try {
        const accounts =
          await backendApis.integrations.getConnectedAccounts(toolkitName);
        const account = accountId
          ? accounts.find((acc) => acc.id === accountId)
          : accounts.find((acc) => acc.connection_status === 'connected');
        if (account?.connection_status === 'connected') settle(true);
        if (account?.connection_status === 'failed') settle(false);
      } catch {
        // Transient fetch errors: keep polling until the timeout.
      }
    };

    // The OAuth result page that posts this message is served by the backend.
    // It does not name the toolkit, so it only triggers a check of this one.
    const trustedOrigin = new URL(getEnvVar('VITE_API_URL')).origin;
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== trustedOrigin) return;
      const data = event.data as { type?: string } | null;
      if (data?.type === 'littlebird:oauth') void checkAccount();
    };
    window.addEventListener('message', onMessage);

    const pollInterval = setInterval(checkAccount, OAUTH_POLL_INTERVAL_MS);

    const timeout = setTimeout(() => settle(false), OAUTH_TIMEOUT_MS);
  });

/**
 * Runs an OAuth connect in `popup` and resolves `true` once the account is
 * connected. `popup` must be opened synchronously in the click handler so
 * popup blockers treat it as user-initiated.
 */
export const startIntegrationConnect = async (
  toolkitName: string,
  popup: Window | null,
  source: string,
  { quickGenerateInitialSuggestions = false } = {}
): Promise<boolean> => {
  try {
    const response = await backendApis.integrations.connectIntegration(
      toolkitName,
      undefined,
      currentPageReturnTo(),
      undefined,
      quickGenerateInitialSuggestions
    );
    if (!response.success || !response.redirect_url) {
      popup?.close();
      return false;
    }

    const oauthPopup = popup ?? openOAuthPopup(response.redirect_url);
    if (!oauthPopup) return false;
    if (popup) popup.location.href = response.redirect_url;

    const success = await waitForOAuthCompletion(
      toolkitName,
      response.account_id ?? null
    );
    // On success the OAuth result page closes itself.
    if (!success) {
      oauthPopup.close();
      return false;
    }

    queryClient.invalidateQueries({ queryKey: integrationKeys.all });
    trackToolkitConnected(toolkitName, source);
    return true;
  } catch (error) {
    console.error('Web onboarding integration connect failed:', error);
    popup?.close();
    return false;
  }
};
